import React from 'react';

import { Box, Checkbox, Divider, FormControlLabel, List, ListItem, ListItemText, Typography, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { AssetItem, RecommendedAsset } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

import AssetListItem from './AssetListItem';

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    maxHeight: 400,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

interface AssetSectionProps {
  title: string;
  assets: (RecommendedAsset | AssetItem)[];
  selectedAssetIds: number[];
  onToggle: (asset: RecommendedAsset | AssetItem) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  emptyMessage?: string[];
  isRecommended?: boolean;
}

const AssetSection: React.FC<AssetSectionProps> = ({
  title,
  assets,
  selectedAssetIds,
  onToggle,
  onSelectAll,
  onDeselectAll,
  emptyMessage,
  isRecommended = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (assets.length === 0 && !emptyMessage) {
    return null;
  }

  const allSelected = assets.length > 0 && assets.every(asset => selectedAssetIds.includes(asset.id));
  const someSelected = assets.some(asset => selectedAssetIds.includes(asset.id)) && !allSelected;

  return (
    <Box mb={4}>
      <Box className={classes.sectionHeader}>
        <Typography variant='h6'>{title}</Typography>
        {assets.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                color='primary'
                indeterminate={someSelected}
                onChange={e => (e.target.checked ? onSelectAll() : onDeselectAll())}
              />
            }
            label={
              allSelected
                ? t(action_library_messages.instance.asset_selection.deselect_all)
                : t(action_library_messages.instance.asset_selection.select_all)
            }
          />
        )}
      </Box>
      <Divider />
      <List className={classes.list} dense>
        {assets.map(asset => (
          <AssetListItem
            key={asset.id}
            asset={asset}
            isRecommended={isRecommended}
            isSelected={selectedAssetIds.includes(asset.id)}
            onToggle={() => onToggle(asset)}
          />
        ))}
        {assets.length === 0 && emptyMessage && (
          <ListItem>
            <ListItemText primary={t(emptyMessage)} />
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export default AssetSection;
